import { FieldValues } from "react-hook-form";

const tokens: string[] = ["f:", "b:", "s:", "i:", "a:"];

enum Operator {
  EQUAL = "=",
  NOT = "!",
  OR = "||",
  AND = "&&",
  CONTAINS = "^",
  CONTAINSONLY = "@",
  ISANY = "isAny",
}

const hasSingleOperand = (exp: Expression): boolean => exp.operands.length == 1;
const hasOperands = (exp: Expression): boolean => exp.operands.length >= 2;
const hasOperator = (exp: Expression): boolean =>
  typeof exp.operator !== "undefined";

//const sectionValueRegEx = /section\((\w+)\).value\((\w+)\)/;
const fieldValueRegEx = /f:(\d*)/;
const stringArrayRegEx = /a:(\d*)/;

const isBooleanToken = (argument: string): boolean => {
  return /b:true/.test(argument);
};

const isToken = (argument: string): boolean =>
  tokens.some((token) => argument.startsWith(token));

const isOperator = (argument: string): boolean =>
  (Object as any).values(Operator).includes(argument);

const isFieldExpression = (argument: string): boolean =>
  fieldValueRegEx.test(argument);

const isStringArrayExpression = (argument: string): boolean =>
  stringArrayRegEx.test(argument);

const getFieldValue = (token: string): RegExpMatchArray | null =>
  token.match(fieldValueRegEx);

type Operand = {
  value: string | any[] | null;
  type: string | null;
};

type Expression = {
  operands: Operand[];
  operator: Operator | undefined;
};

const flushOperands = (exp: Expression, argArray: string[]): void => {
  while (exp.operands.length > 0) {
    const operand: Operand = exp.operands.pop() ?? { value: "", type: "s" };
    const operandToken: string =
      (operand.type != null ? operand.type : "") +
      ":" +
      (operand.value != null ? operand.value : "");
    argArray.unshift(operandToken);
  }
};

export const evaluateExpression = (
  argArray: string[],
  values: FieldValues
): boolean => {
  let exp: Expression = {
    operands: [],
    operator: undefined,
  };

  // Nothing to evaluate
  if (argArray.length == 0) return false;

  // Creates a valid Expression to evaluate
  while (!exp.operator && argArray.length != 0) {
    const argument: string | undefined = argArray.shift();
    if (argument) {
      if (isToken(argument)) {
        if (isFieldExpression(argument)) {
          const fieldId: RegExpMatchArray =
            getFieldValue(argument) ?? ({} as RegExpMatchArray);
          const fieldValue: any = values[fieldId[1]];
          let theValue: string | string[] | undefined = undefined;
          let type: string | undefined = undefined;
          if (fieldValue && Array.isArray(fieldValue)) {
            theValue = fieldValue.filter(
              (value: any) => value && value.hasOwnProperty("id")
            );
            type = "a";
          } else if (fieldValue && fieldValue.hasOwnProperty("id")) {
            theValue = fieldValue.id;
            type = "s";
          } else {
            theValue = fieldValue;
            type = "s";
          }
          const oper: Operand = theValue
            ? { value: theValue, type: type }
            : { value: null, type: null };
          exp.operands.push(oper);
        } else if (isStringArrayExpression(argument)) {
          exp.operands.push({
            value: argument.substring(2).split(","),
            type: "a",
          });
        } else {
          exp.operands.push({
            value: argument.substring(2),
            type: argument.substring(0, 1),
          });
        }
      }

      if (isOperator(argument)) {
        exp.operator = argument as Operator;
      }
    }
  }

  //Case of two operands and an operator
  if (hasOperands(exp) && hasOperator(exp)) {
    const secondOperand: Operand = exp.operands.pop() ?? {
      value: null,
      type: null,
    };
    const firstOperand: Operand = exp.operands.pop() ?? {
      value: null,
      type: null,
    };
    let result: Boolean = new Boolean(false);
    switch (exp.operator?.toString()) {
      case Operator.EQUAL:
        if (
          Array.isArray(firstOperand.value) &&
          firstOperand.value.length == 1
        ) {
          result = firstOperand.value[0].id === secondOperand.value;
        } else {
          result = new Boolean(firstOperand.value === secondOperand.value);
        }
        argArray.unshift("b:" + result.toString());
        flushOperands(exp, argArray);
        break;
      case Operator.AND:
        if (firstOperand.type !== "a" && secondOperand.type !== "a") {
          result = new Boolean(
            firstOperand.value != null &&
              (firstOperand.value as string).match(/^(true|y)$/) &&
              secondOperand.value != null &&
              (secondOperand.value as string).match(/^(true|y)$/)
          );
        }
        argArray.unshift("b:" + result.toString());
        flushOperands(exp, argArray);
        break;
      case Operator.OR:
        if (firstOperand.type !== "a" && secondOperand.type !== "a") {
          result = new Boolean(
            (firstOperand.value != null &&
              (firstOperand.value as string).match(/^(true|y)$/)) ||
              (secondOperand.value != null &&
                (secondOperand.value as string).match(/^(true|y)$/))
          );
        }
        argArray.unshift("b:" + result.toString());
        flushOperands(exp, argArray);
        break;
      case Operator.CONTAINSONLY:
        if (firstOperand.type == "a") {
          result =
            (firstOperand.value as any[]).length == 1 &&
            (firstOperand.value as any[])[0].id == secondOperand.value;
        }
        argArray.unshift("b:" + result.toString());
        flushOperands(exp, argArray);
        break;
      case Operator.CONTAINS:
        if (firstOperand.type == "a") {
          result = !!(firstOperand.value as any[]).find(
            (e: { id: string; value: string }) =>
              (secondOperand.value as string) == e.id
          );
        }
        argArray.unshift("b:" + result.toString());
        flushOperands(exp, argArray);
        break;
      case Operator.ISANY:
        if (firstOperand.type !== "a") {
          result =
            firstOperand.value == null ||
            !!(secondOperand.value as any[]).find((e: string) => {
              return (firstOperand.value as string) == e;
            });
        }
        argArray.unshift("b:" + result.toString());
        flushOperands(exp, argArray);
        break;
      default:
        argArray.unshift("b:" + new Boolean(false).toString());
    }
  } // Case single operator and operand
  else if (hasSingleOperand(exp) && hasOperator(exp)) {
    switch (exp.operator?.toString()) {
      case Operator.NOT:
        argArray.unshift("b:" + new Boolean(exp.operands[0].value).toString());
        break;
      default:
        argArray.unshift("b:" + new Boolean(false).toString());
    }
  } else {
    if (typeof exp.operator == "undefined") {
      return /^true$/i.test((exp.operands[0].value as string) ?? "");
    } else {
      return false;
    }
  }
  let ret: boolean = evaluateExpression(argArray, values);
  return ret;
};
